import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { siteConfig } from '@/constants/site'

const sections = [
    {
        title: 'Quem somos',
        paragraphs: [
            `Esta política explica como a ${siteConfig.name} trata os dados pessoais enviados por este site, de acordo com a Lei Geral de Proteção de Dados (Lei 13.709/2018).`
        ]
    },
    {
        title: 'Quais dados coletamos',
        paragraphs: [
            'Quando você pede uma proposta, coletamos o nome, o WhatsApp, o e-mail, o tipo de consórcio de interesse, o valor e o prazo simulados e a mensagem que você escrever, se houver.',
            'Também registramos estatísticas de visitas de forma anônima, sem cookies e sem identificar quem visita o site.'
        ]
    },
    {
        title: 'Para que usamos os seus dados',
        paragraphs: [
            'Usamos as informações para entrar em contato com você, preparar e enviar propostas de consórcio e tirar as suas dúvidas. Não vendemos nem alugamos os seus dados.'
        ]
    },
    {
        title: 'Base legal',
        paragraphs: [
            'Tratamos os seus dados com base no consentimento que você dá ao marcar a caixa de aceite do formulário e na realização de procedimentos preliminares a uma possível contratação, conforme o artigo 7º, incisos I e V, da LGPD.'
        ]
    },
    {
        title: 'Com quem compartilhamos',
        paragraphs: [
            'Os dados podem ser compartilhados com a administradora de consórcio escolhida, apenas para elaborar a proposta e concluir uma eventual contratação, e com os fornecedores que mantêm este site no ar, como hospedagem e envio de e-mails.'
        ]
    },
    {
        title: 'E-mails',
        paragraphs: [
            'Os pedidos enviados pelo site geram uma notificação por e-mail para a nossa equipe. Os e-mails enviados por este site podem registrar aberturas e cliques em links, para medir a entrega e o interesse nas mensagens.'
        ]
    },
    {
        title: 'Por quanto tempo guardamos',
        paragraphs: [
            'Guardamos os seus dados pelo tempo necessário para o atendimento e, depois disso, apenas pelo prazo exigido por lei. Você pode pedir a exclusão a qualquer momento.'
        ]
    },
    {
        title: 'Seus direitos',
        paragraphs: [
            'Você pode pedir a confirmação do tratamento, o acesso, a correção, a anonimização, a portabilidade e a exclusão dos seus dados, além de revogar o consentimento, conforme o artigo 18 da LGPD.'
        ]
    },
    {
        title: 'Segurança',
        paragraphs: [
            'As informações trafegam com criptografia e ficam armazenadas em serviços com controle de acesso. Apenas as pessoas responsáveis pelo seu atendimento podem consultá-las.'
        ]
    },
    {
        title: 'Atualizações desta política',
        paragraphs: [
            'Esta política pode ser atualizada para refletir mudanças no site ou na legislação. A versão em vigor é sempre a publicada nesta página.'
        ]
    }
]

const PrivacyPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Política de privacidade'
                description={`Saiba como a ${siteConfig.name} coleta, usa e protege os dados pessoais enviados pelo site, conforme a LGPD.`}
            />

            <article className='mx-auto max-w-3xl px-5 pb-24 pt-10 sm:px-8 sm:pb-32 sm:pt-16'>
                <p className='inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-bronze'>
                    <span aria-hidden='true' className='h-px w-8 bg-current' />
                    LGPD
                </p>

                <h1 className='mt-5 font-heading text-5xl leading-[1.02] tracking-tight text-foreground sm:text-6xl'>
                    Política de privacidade
                </h1>

                <p className='mt-6 text-lg leading-relaxed text-muted-foreground'>
                    Cuidar dos seus dados faz parte de um bom planejamento. Aqui você encontra, de forma direta, o que fazemos com as informações que recebemos pelo site.
                </p>

                <p className='mt-4 text-sm text-muted-foreground'>
                    Última atualização: novembro de 2026.
                </p>

                <div className='mt-12 grid gap-10 border-t border-border pt-12'>
                    {sections.map(section => (
                        <section key={section.title}>
                            <h2 className='text-xl font-bold text-foreground'>
                                {section.title}
                            </h2>

                            {section.paragraphs.map(paragraph => (
                                <p key={paragraph} className='mt-3 leading-relaxed text-muted-foreground'>
                                    {paragraph}
                                </p>
                            ))}
                        </section>
                    ))}

                    <section>
                        <h2 className='text-xl font-bold text-foreground'>
                            Como exercer os seus direitos
                        </h2>

                        <p className='mt-3 leading-relaxed text-muted-foreground'>
                            Envie o seu pedido pelo{' '}
                            <Link
                                href='/#contato'
                                className='rounded font-semibold text-foreground underline underline-offset-4 hover:text-bronze focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                            >
                                formulário de contato
                            </Link>
                            {' '}informando o que deseja. Responderemos no menor prazo possível.
                        </p>
                    </section>
                </div>
            </article>
        </Layout>
    )
}

export default PrivacyPage
